import { MessageType } from '@wix/ambassador-innovation-widget-v1-message/types';

import { clearNavigationPayload } from '@/utils';

import { PayloadAction } from 'AiAssistantWidget/Widget/components/ChatMessagePayload/types';

import { createAppAsyncThunk } from '../createAppAsyncThunk';
import {
  ActionMessageType,
  addActionMessage,
  selectMessages,
} from '../messages';
import { selectComposed } from '../composed-selectors';
import { selectLayout } from '../layout';
import { selectSettings } from '../settings';

import { getStorageReportList, setStorageReportList } from './utils';

type ReportInternalNavigation = {
  id: string;
  path: string;
  source: 'chat' | 'website';
};

export const reportNavigation = createAppAsyncThunk(
  'engineActions/reportNavigation',
  async (
    { id, path, source }: ReportInternalNavigation,
    { dispatch, extra: { t, experiments }, getState },
  ) => {
    const state = getState();
    const isOnline = selectComposed.maybeWidgetOnline(state);
    const historyLoadedOnce = selectMessages.isLoaded(state);
    const settingsLoadedOnce = selectSettings.isLoaded(state);
    const hasOngoingConversation = selectMessages.hasOngoingConversation(state);

    const isVisible = selectLayout.isVisible(state);

    const isReportingEnabled = selectSettings.isReportingEnabled(state);
    const isReportExperimentEnabled = experiments.enabled(
      'specs.aiAssistant.reportNavigationToEngine',
    );

    if (!historyLoadedOnce || !settingsLoadedOnce || isOnline === undefined) {
      return setTimeout(() => {
        dispatch(reportNavigation({ id, path, source }));
      }, 500);
    }

    const shouldReportNavigation =
      isReportingEnabled &&
      isReportExperimentEnabled &&
      historyLoadedOnce &&
      hasOngoingConversation &&
      isOnline &&
      (source === 'chat' ? true : isVisible);

    const ensuredPath = path ? path : '/';
    const ensuredId = id ? id : '/';

    const reports = getStorageReportList();

    if (!shouldReportNavigation || reports.includes(ensuredPath)) {
      return Promise.reject(new Error('No need to report navigation'));
    }

    clearNavigationPayload();

    setStorageReportList([...reports, ensuredPath]);

    dispatch(
      addActionMessage({
        messageType: MessageType.SITE_NAVIGATION,
        data: {
          type: ActionMessageType.Navigation,
          action: `${PayloadAction.VIEW}:${ensuredId}`,
        },
        mainText: t('app.widget.chat.inboxNavigationMessage', {
          path: ensuredPath,
        }),
      }),
    );
  },
);

export const addToCart = createAppAsyncThunk(
  'engineActions/addToCart',
  async (
    { id, name }: { id: string; name: string },
    { dispatch, extra: { t } },
  ) => {
    dispatch(
      addActionMessage({
        messageType: MessageType.ADD_TO_CART,
        data: {
          type: ActionMessageType.AddToCart,
          action: `${PayloadAction.ADD_TO_CART}:${id}`,
        },
        mainText: t('app.widget.chat.inboxAddToCart', { name }),
      }),
    );
  },
);

export const bookService = createAppAsyncThunk(
  'engineActions/bookService',
  async (
    { id, name }: { id: string; name: string },
    { dispatch, extra: { t } },
  ) => {
    dispatch(
      addActionMessage({
        messageType: MessageType.BOOK_SERVICE,
        data: {
          type: ActionMessageType.BookService,
          action: `${PayloadAction.BOOK_SERVICE}:${id}`,
        },
        mainText: t('app.widget.chat.bookService', { name }),
      }),
    );
  },
);
